import { useEffect, useState } from 'react'
import { useAdminAlertConfig, useUpdateAdminAlertConfig } from '../../../hooks/admin/useCatchAllAlerts'
import type { AdminAlertConfig } from '../../../services/admin/alertsApi'

type FormState = Omit<AdminAlertConfig, 'id'>

export default function SettingsPage() {
  const { data: config, isLoading } = useAdminAlertConfig()
  const updateConfig = useUpdateAdminAlertConfig()
  const [form, setForm] = useState<FormState | null>(null)

  useEffect(() => {
    if (config) {
      const { id, ...rest } = config
      setForm(rest)
    }
  }, [config])

  if (isLoading || !form) return <p className="text-gray-600">Loading settings...</p>

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev))

  const handleSave = () => updateConfig.mutate(form)

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold">Catch-All Settings</h1>
        <p className="text-gray-600 mt-1">
          Controls the admin catch-all monitor that watches <span className="font-medium">all</span> devices,
          independent of who owns them.
        </p>
      </div>

      {/* Master switch */}
      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => set('enabled', e.target.checked)}
            className="rounded border-gray-300 h-5 w-5"
          />
          <span className="text-lg font-semibold">Enable catch-all monitoring</span>
        </label>
        <p className="text-gray-500 text-sm mt-2">
          Requires the <code>admin_catch_all_alerts</code> feature flag to also be on in the processor.
        </p>
      </section>

      {/* Alert types */}
      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-3">
        <h2 className="text-lg font-semibold">Alert types</h2>
        <Toggle label="Device offline" checked={form.offlineEnabled} onChange={(v) => set('offlineEnabled', v)} />
        <Toggle label="Battery low" checked={form.batteryLowEnabled} onChange={(v) => set('batteryLowEnabled', v)} />
        <Toggle label="Battery no power" checked={form.batteryNoPowerEnabled} onChange={(v) => set('batteryNoPowerEnabled', v)} />
        <Toggle label="Recovery (back online / battery recovered)" checked={form.recoveryEnabled} onChange={(v) => set('recoveryEnabled', v)} />
        <div className="border-t border-gray-100 pt-3">
          <Toggle
            label="Sensor threshold alerts (mirrored from owners)"
            checked={form.sensorAlertsEnabled}
            onChange={(v) => set('sensorAlertsEnabled', v)}
          />
          <p className="text-gray-500 text-sm mt-1 ml-7">
            Off by default. When on, admins also receive a copy of any sensor-threshold alert that fires for a device owner.
          </p>
        </div>
      </section>

      {/* Signal strength */}
      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
        <h2 className="text-lg font-semibold">Signal strength</h2>
        <Toggle
          label="Signal-strength alerts"
          checked={form.signalAlertsEnabled}
          onChange={(v) => set('signalAlertsEnabled', v)}
        />
        <p className="text-gray-500 text-sm ml-7 -mt-2">
          Weak RSSI margin, and degradation against a device's own baseline. Produces nothing
          until the Sigfox callback is configured to send <code>rssi</code>/<code>snr</code>/
          <code>station</code>.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <NumberField
            label="Weak below margin (dB)"
            value={form.weakMarginDb}
            step={1}
            onChange={(v) => set('weakMarginDb', v)}
          />
          <NumberField
            label="Degraded after drop (dB)"
            value={form.degradedDropDb}
            step={1}
            onChange={(v) => set('degradedDropDb', v)}
          />
          <NumberField
            label="Sustained checks before firing"
            value={form.signalSustainedChecks}
            step={1}
            onChange={(v) => set('signalSustainedChecks', v)}
          />
          <NumberField
            label="Minimum samples to judge"
            value={form.signalMinSamples}
            step={1}
            onChange={(v) => set('signalMinSamples', v)}
          />
        </div>
        <p className="text-gray-500 text-sm">
          Margin is measured above the −142 dBm demodulation floor, so 6 dB means a median of
          −136 dBm or worse. Degradation compares the last 7 days against the previous 30.
          The minimum matters for the same reason it does below: a median taken over one
          reading is not a median, and without a floor a device that transmitted once would
          page on that single sample. Below it the device is left to the offline alert.
        </p>
      </section>

      {/* Missing readings */}
      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
        <h2 className="text-lg font-semibold">Missing readings</h2>
        <Toggle
          label="Missing-readings alerts"
          checked={form.missingReadingsEnabled}
          onChange={(v) => set('missingReadingsEnabled', v)}
        />
        <p className="text-gray-500 text-sm ml-7 -mt-2">
          Devices losing a high share of their transmissions, measured from gaps in the Sigfox
          sequence counter. Needs no extra data — it works on history you already have.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <NumberField
            label="Alert above % missing"
            value={form.missingPercentThreshold}
            step={1}
            onChange={(v) => set('missingPercentThreshold', v)}
          />
          <NumberField
            label="Minimum transmissions to judge"
            value={form.missingMinTransmissions}
            step={1}
            onChange={(v) => set('missingMinTransmissions', v)}
          />
          <NumberField
            label="Sustained checks before firing"
            value={form.missingSustainedChecks}
            step={1}
            onChange={(v) => set('missingSustainedChecks', v)}
          />
        </div>
        <p className="text-gray-500 text-sm">
          The minimum matters: gaps are only visible <em>between</em> messages, so a device that
          has gone almost silent scores 0% missing and would otherwise look perfectly healthy.
          Below this many transmissions the device is left to the offline alert instead.
        </p>
      </section>

      {/* Thresholds */}
      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
        <h2 className="text-lg font-semibold">Thresholds</h2>
        <div className="grid grid-cols-2 gap-4">
          <NumberField label="Low battery (V)" value={form.lowBatteryThreshold} step={0.1} onChange={(v) => set('lowBatteryThreshold', v)} />
          <NumberField label="No power (V)" value={form.noPowerThreshold} step={0.1} onChange={(v) => set('noPowerThreshold', v)} />
          <NumberField label="Offline threshold (minutes)" value={form.offlineThresholdMinutes} step={1} onChange={(v) => set('offlineThresholdMinutes', v)} />
          <NumberField label="Retry budget (on send failure)" value={form.retryBudget} step={1} onChange={(v) => set('retryBudget', v)} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Offline modes monitored</label>
          <select
            value={form.offlineModesMonitored}
            onChange={(e) => set('offlineModesMonitored', e.target.value as FormState['offlineModesMonitored'])}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="scheduled">Scheduled only</option>
            <option value="config">Config only</option>
            <option value="all">All modes</option>
          </select>
        </div>
      </section>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={updateConfig.isPending}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {updateConfig.isPending ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  )
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="rounded border-gray-300 h-4 w-4" />
      <span>{label}</span>
    </label>
  )
}

function NumberField({
  label,
  value,
  step,
  onChange,
}: {
  label: string
  value: number
  step: number
  onChange: (v: number) => void
}) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <input
        type="number"
        value={value}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
      />
    </div>
  )
}
